import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { AtlassianClient } from './atlassian-client';
import { type ExtendedFetcher } from './atlassian-client-types';
import { DelegatingApolloClient } from './delegating-apollo-client';
import { type DelegatingClient } from './delegating-client-types';
import { DelegatingGraphQLClient } from './delegating-graphql-client';
import { type QueryResult } from './graphql/types';
import { isError } from './is-error';
import {
  type JiraGraphQLCreateIssueFieldsVariables,
  type JiraCreateIssueRequest,
  type JiraCreateIssueFields,
  type JiraCreateIssueResponse,
  type JiraGraphQLCreateIssueFieldsResponse,
  type JiraRestProjectResponse,
  type JiraCreateRemoteLinkOpts,
  type JiraCreateRemoteLinkRequest,
  type JiraGraphQLRequest,
  type JiraIssueListQueryResponse,
  type JiraCreateIssueLinkOpts,
  type JiraCreateIssueLinkRequest,
  type JiraDeleteIssueLinkByIdOpts,
  type JiraGetLinkedIssuesOpts,
  type JiraGetLinkedIssuesResponse,
  type JiraDeleteIssueLinkOpts,
  type JiraGetIssueLinksOpts,
  type JiraIssueLink,
  type JiraIssueLinkTypesQueryResponse,
  type JiraEditIssueRequest,
  type JiraEditIssueFields,
  type JiraEditIssueResponse,
  type JiraRestSingleProjectResponse,
  type JiraGraphQLParentCandidatesWithInfoForNewIssueVariables,
  type JiraGraphQLParentCandidatesWithInfoForNewIssueResponse,
  type JiraPaginationInfo,
  type JiraIssuesByIdsQueryResponse,
  type JiraRestProjectVersion,
  type JiraNaturalLanguageToJqlQueryVariables,
  type JiraNaturalLanguageToJqlQueryResponse,
  type JiraIssuesByKeyWithLinksQueryResponse,
  type JiraIssuesByKeyWithLinksQueryVariables,
  type JiraIssuesByKeyWithChildrenQueryResponse,
  type JiraIssuesByKeyWithChildrenQueryVariables,
  type JiraIssueSearchStableResponse,
  type JiraIssueSearchStableEdge,
  type JiraIssueListQueryNode,
  type JiraGetFieldsForProjectsRequest,
  type JiraGetFieldsForProjectsResponse,
} from './jira-client-types';
import { issueIdsQuery, issuesByIdsQuery } from './queries/issue-ids-query';
import { issueLinkTypesQuery } from './queries/issue-link-types-query';
import { issueSearchStableQuery } from './queries/issue-list-query';
import * as createJiraIssueCreateFieldsQuery from './queries/jira-issue-create-fields-query.graphql';
import * as issuesByKeyWithChildrenQuery from './queries/jira-issues-by-key-with-children.graphql';
import * as issuesByKeyWithLinksQuery from './queries/jira-issues-by-key-with-links.graphql';
import * as naturalLanguageToJqlQuery from './queries/jira-natural-language-to-jql-query.graphql';
import * as parentCandidatesForNewIssueQuery from './queries/parent-candidates-with-info-for-new-issue-query.graphql';

const FIVE_MINUTES = 5 * 60 * 1000;

function createGraphQLClient(fetcher: ExtendedFetcher): DelegatingClient {
  if (isFeatureFlagEnabled('useNewGraphQLClient')) {
    return new DelegatingGraphQLClient(fetcher);
  }
  return new DelegatingApolloClient(fetcher);
}

export class JiraClient {
  private atlassianClient: AtlassianClient;

  private readonly clients: DelegatingClient;

  constructor(fetcher: ExtendedFetcher) {
    this.atlassianClient = new AtlassianClient(fetcher);
    this.clients = createGraphQLClient(fetcher);
  }

  public getProjects(
    baseUrl: URL,
    query = '',
    options: { startAt?: number; maxResults?: number } = {}
  ) {
    const { startAt = 0, maxResults = 100 } = options;

    return this.atlassianClient.get<JiraRestProjectResponse>(
      `${baseUrl}/rest/api/3/project/search`,
      {
        query,
        startAt: startAt.toString(),
        maxResults: maxResults.toString(),
      },
      { strategy: 'stale-while-revalidate', lifetime: FIVE_MINUTES },
      { requestType: 'jiraGetProjects' }
    );
  }

  public getProject(baseUrl: URL, projectKeyOrId: string) {
    return this.atlassianClient.get<JiraRestSingleProjectResponse>(
      `${baseUrl}/rest/api/3/project/${encodeURIComponent(projectKeyOrId)}`,
      {},
      { strategy: 'stale-while-revalidate', lifetime: FIVE_MINUTES },
      { requestType: 'jiraGetProject' }
    );
  }

  public getIssueCreateFields(
    baseUrl: URL,
    variables: JiraGraphQLCreateIssueFieldsVariables
  ): Promise<QueryResult<JiraGraphQLCreateIssueFieldsResponse['data']>> {
    return this.clients
      .getOrCreate(baseUrl.toString(), '/rest/gira/1')
      .query<JiraGraphQLCreateIssueFieldsResponse['data']>({
        query: createJiraIssueCreateFieldsQuery.JiraGlobalIssueCreateModalLoad,
        variables,
        context: {
          tenantBaseUrl: baseUrl.toString(),
        },
      });
  }

  /**
   * Returns a paginated list of fields for the requested projects and work types.
   * See: https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-fields/#api-rest-api-3-projects-fields-get
   */
  public async getFieldsForProjects(
    baseUrl: URL,
    opts: JiraGetFieldsForProjectsRequest,
    pagination?: { startAt: number; maxResults: number }
  ): Promise<JiraGetFieldsForProjectsResponse> {
    const searchParams = new URLSearchParams();
    opts.projectId.forEach((id) => searchParams.append('projectId', String(id)));
    opts.workTypeId.forEach((id) => searchParams.append('workTypeId', String(id)));
    if (pagination !== undefined) {
      searchParams.set('startAt', String(pagination.startAt));
      searchParams.set('maxResults', String(pagination.maxResults));
    }
    const url = `${baseUrl}/rest/api/3/projects/fields?${searchParams.toString()}`;
    return this.atlassianClient.get<JiraGetFieldsForProjectsResponse>(
      url,
      undefined,
      { strategy: 'stale-while-revalidate', lifetime: FIVE_MINUTES },
      { requestType: 'jiraGetFieldsForProjects' }
    );
  }

  public async createIssue(baseUrl: URL, fields: JiraCreateIssueFields, update = {}) {
    return this.atlassianClient.post<JiraCreateIssueRequest, JiraCreateIssueResponse>(
      `${baseUrl}/rest/api/3/issue`,
      {
        update,
        fields,
      },
      { requestType: 'jiraCreateIssue' }
    );
  }

  public async createRemoteLink(baseUrl: URL, opts: JiraCreateRemoteLinkOpts) {
    return this.atlassianClient.post<JiraCreateRemoteLinkRequest, void>(
      `${baseUrl}/rest/api/3/issue/${opts.issueKey}/remotelink`,
      {
        object: {
          icon: { url16x16: opts.icon },
          title: opts.title,
          url: opts.url.href,
        },
      },
      { requestType: 'jiraCreateRemoteLink' }
    );
  }

  public async createIssueLink(baseUrl: URL, opts: JiraCreateIssueLinkOpts) {
    return this.atlassianClient.postWithoutResponse<JiraCreateIssueLinkRequest>(
      `${baseUrl}/rest/api/3/issueLink`,
      {
        type: { id: opts.type },
        inwardIssue: { key: opts.inwardIssue },
        outwardIssue: { key: opts.outwardIssue },
      },
      undefined,
      { requestType: 'jiraCreateIssueLink' }
    );
  }

  /**
   * Edits an issue by issue key with the given fields and update object.
   * @see https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issues/#api-rest-api-3-issue-issueidorkey-put
   */
  public async editIssue(
    baseUrl: URL,
    issueKey: string,
    fields: JiraEditIssueFields,
    update = {}
  ) {
    return this.atlassianClient.put<JiraEditIssueRequest, JiraEditIssueResponse>(
      `${baseUrl}/rest/api/3/issue/${issueKey}`,
      {
        update,
        fields,
      },
      { requestType: 'jiraEditLink' }
    );
  }

  /**
   * Gets an issue link by link ID
   *
   * @remark Currently using a REST endpoint as specified in the [issue links
   * documentation](https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-links#api-rest-api-3-issuelink-linkid-get).
   * Could potentially move to `rest/gira/1` in the future
   */
  public async getIssueLinkById(baseUrl: URL, linkId: string) {
    return this.atlassianClient.get<JiraIssueLink>(
      `${baseUrl}/rest/api/3/issueLink/${linkId}`,
      undefined,
      undefined,
      { requestType: 'jiraGetIssueLinkById' }
    );
  }

  /**
   * Gets all linked issues for a given issue.
   *
   * @remark Currently using a REST endpoint as specified in the [issue links
   * documentation](https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-links/#api-group-issue-links).
   * Could potentially move to `rest/gira/1` in the future once `issueByKey` is
   * no longer experimental.
   *
   * @see https://product-fabric.atlassian.net/browse/CNS-2891?focusedCommentId=313037
   */
  public async getLinkedIssues(baseUrl: URL, opts: JiraGetLinkedIssuesOpts) {
    return this.atlassianClient.get<JiraGetLinkedIssuesResponse>(
      `${baseUrl}/rest/api/3/issue/${opts.linkedIssueKey}`,
      { fields: 'issuelinks' },
      undefined,
      { requestType: 'jiraGetLinkedIssues' }
    );
  }

  /**
   * Gets the issue links between two given issues.
   */
  public async getIssueLinks(baseUrl: URL, opts: JiraGetIssueLinksOpts) {
    const allLinks = await this.getLinkedIssues(baseUrl, {
      linkedIssueKey: opts.sourceIssueKey,
    });
    const issueLinks = allLinks.fields.issuelinks.filter(
      (issue) =>
        opts.targetIssueKey === issue.outwardIssue?.key ||
        opts.targetIssueKey === issue.inwardIssue?.key
    );
    return issueLinks;
  }

  /**
   * Gets the available issue link types for a given site.
   */
  public async getIssueLinkTypes(baseUrl: URL) {
    const delimiter = baseUrl.href.endsWith('/') ? '' : '/';
    const result = await this.atlassianClient.post<
      JiraGraphQLRequest,
      JiraIssueLinkTypesQueryResponse
    >(
      [baseUrl, 'rest/gira/1'].join(delimiter),
      {
        operationName: 'IssueLinkTypesQuery',
        query: issueLinkTypesQuery,
        variables: {},
      },
      undefined,
      { requestType: 'jiraGetIssueLinkTypes' }
    );
    return result;
  }

  public async deleteIssueLinkById(baseUrl: URL, opts: JiraDeleteIssueLinkByIdOpts) {
    return this.atlassianClient.delete(`${baseUrl}/rest/api/3/issueLink/${opts.linkId}`, {
      requestType: 'jiraDeleteIssueLinkById',
    });
  }

  public async deleteIssueLink(baseUrl: URL, opts: JiraDeleteIssueLinkOpts) {
    const issueLinks = await this.getIssueLinks(baseUrl, opts);
    const linkToDelete = issueLinks.find((issue) => opts.linkTypeId === issue.type.id);
    if (!linkToDelete) {
      return;
    }
    await this.deleteIssueLinkById(baseUrl, { linkId: linkToDelete.id });
  }

  public async queryIssues(
    baseUrl: URL,
    cloudId: string,
    jql: string,
    options: {
      page: number;
      pageSize: number;
      signal?: AbortSignal;
      paginationInfo?: JiraPaginationInfo;
    }
  ) {
    const delimiter = baseUrl.href.endsWith('/') ? '' : '/';
    const page = options?.page ?? 0;
    let first: number | undefined;
    let last: number | undefined;
    let after: string | undefined;
    let before: string | undefined;

    if (options.paginationInfo) {
      if (page === options.paginationInfo.page + 1 && options.paginationInfo.endCursor) {
        // Going forward by 1 page
        after = options.paginationInfo.endCursor;
        first = options.pageSize;
      } else if (
        page == options.paginationInfo.page - 1 &&
        options.paginationInfo.startCursor
      ) {
        // Going backwards by 1 page
        before = options.paginationInfo.startCursor;
        last = options.pageSize;
      } else {
        // Jumping to a page more than 1 page away
        first = options.pageSize * (page + 1);
        last = options.pageSize;
      }
    } else {
      first = options.pageSize * (page + 1);
    }

    const variables: Record<string, unknown> = {
      cloudId,
      jql,
      ...(first !== undefined && { first }),
      ...(last !== undefined && { last }),
      ...(after && { after }),
      ...(before && { before }),
    };

    const result = await this.atlassianClient.post<
      JiraGraphQLRequest,
      JiraIssueSearchStableResponse
    >(
      [baseUrl, 'rest/gira/1'].join(delimiter),
      {
        operationName: 'IssueSearchStableQuery',
        query: issueSearchStableQuery,
        variables,
      },
      { 'X-ExperimentalApi': 'JiraIssueSearch' },
      {
        requestType: 'jiraQueryIssues',
      }
    );

    if (isError(result)) {
      return result;
    }

    const rawData = result.data?.jira?.issueSearchStable;
    if (!rawData) {
      return {
        data: {
          issues: {
            nodes: [],
            totalCount: 0,
            pageSize: 0,
            jql,
            pageInfo: { page: 0, hasNextPage: false, hasPreviousPage: false },
          },
        },
      };
    }

    const transformedNodes: JiraIssueListQueryNode[] = rawData.edges.map(
      (edge: JiraIssueSearchStableEdge) => {
        const node = edge.node;

        let iconUrlHref = '';
        try {
          const iconUrl = new URL(node.issueType.avatar.medium);

          iconUrl.host = baseUrl.host;
          iconUrl.pathname = `/gateway/api${iconUrl.pathname}`;
          iconUrlHref = iconUrl.href;
        } catch (error) {
          console.error(
            `Invalid icon URL for issue type ${node.issueType.name}: ${node.issueType.avatar.medium}`,
            error
          );
          iconUrlHref = '';
        }

        return {
          id: node.id,
          issueId: node.issueId,
          issuekey: { stringValue: node.key },
          assignee: {
            userValue: node.assigneeField?.user
              ? {
                  accountId: node.assigneeField.user.accountId,
                  avatarUrl: node.assigneeField.user.picture,
                  displayName: node.assigneeField.user.name,
                }
              : {
                  accountId: '',
                  avatarUrl: '',
                  displayName: '',
                },
          },
          issuetype: {
            iconUrl: iconUrlHref,
            name: node.issueType.name,
            backupIconUrl: node.issueType.avatar.medium,
          },
          summary: {
            textValue: node.summary,
          },
          status: {
            name: node.status.name,
            statusId: node.status.statusId,
            statusCategoryId: node.status.statusCategory.statusCategoryId,
          },
        };
      }
    );

    const transformedResponse: JiraIssueListQueryResponse = {
      data: {
        issues: {
          nodes: transformedNodes,
          totalCount: rawData.totalIssueSearchResultCount,
          pageSize: transformedNodes.length,
          jql,
          pageInfo: { ...rawData.pageInfo, page: page },
        },
      },
    };

    return transformedResponse;
  }

  public async queryFirst1000IssueIds(baseUrl: URL, jql: string) {
    const delimiter = baseUrl.href.endsWith('/') ? '' : '/';

    const result = await this.atlassianClient.post<
      JiraGraphQLRequest,
      JiraIssueListQueryResponse
    >(
      [baseUrl, 'rest/gira/1'].join(delimiter),
      {
        operationName: 'IssueIdsQuery',
        query: issueIdsQuery,
        variables: {
          isMaxResultsLimitEnabled: true,
          jql,
          first: 1000,
        },
      },
      undefined,
      {
        requestType: 'jiraQueryIssues',
      }
    );

    if (isError(result)) {
      return result;
    }

    return result;
  }

  public async queryIssuesByIds(baseUrl: URL, ids: number[]) {
    const delimiter = baseUrl.href.endsWith('/') ? '' : '/';

    const result = await this.atlassianClient.post<
      JiraGraphQLRequest,
      JiraIssuesByIdsQueryResponse
    >(
      [baseUrl, 'rest/gira/1'].join(delimiter),
      {
        operationName: 'IssuesByIdsQuery',
        query: issuesByIdsQuery,
        variables: {
          issueIds: ids,
        },
      },
      undefined,
      {
        requestType: 'jiraQueryIssues',
      }
    );

    if (isError(result)) {
      return result;
    }

    return result;
  }

  public getProjectVersions(baseUrl: URL, projectIdOrKey: string) {
    return this.atlassianClient.get<JiraRestProjectVersion[]>(
      `${baseUrl}/rest/api/2/project/${encodeURIComponent(projectIdOrKey)}/versions`,
      {},
      { strategy: 'stale-while-revalidate', lifetime: FIVE_MINUTES },
      { requestType: 'jiraGetProjectVersions' }
    );
  }

  /**
   * Delegates to the Atlassian client to resolve a search field and cache
   * the result
   */
  public async resolveSearchField<T>(
    searchUrl: string,
    {
      query,
      projectId,
      maxResults,
      startAt,
    }: {
      query: string;
      projectId?: string | number;
      maxResults?: number;
      startAt?: number;
    }
  ) {
    return this.atlassianClient.get<T>(
      searchUrl,
      {
        query,
        // We query using projectKey as the query param, not projectId
        ...(projectId ? { projectKey: `${projectId}` } : {}),
        ...(maxResults !== undefined ? { maxResults: `${maxResults}` } : {}),
        ...(startAt !== undefined ? { startAt: `${startAt}` } : {}),
      },
      { strategy: 'stale-while-revalidate', lifetime: FIVE_MINUTES },
      {
        requestType: 'jiraResolveSearchField',
      }
    );
  }

  /**
   * Delegates to the Atlassian client to resolve Parent fields given a
   * projectId, cloudId, issueTypeId and optionally a searchTerm, then cache the result
   */
  public async resolveParentCandidatesForNewIssue(
    baseUrl: URL,
    variables: JiraGraphQLParentCandidatesWithInfoForNewIssueVariables
  ) {
    return this.clients
      .getOrCreate(baseUrl.toString(), '/rest/gira/1')
      .query<JiraGraphQLParentCandidatesWithInfoForNewIssueResponse['data']>({
        query: parentCandidatesForNewIssueQuery.ParentCandidates,
        variables,
        context: {
          tenantBaseUrl: baseUrl.toString(),
        },
      });
  }

  /**
   * Transforms natural language into JQL using the product GraphQL endpoint.
   * Uses the site origin (not the tenant ex path) as the Apollo base URL.
   */
  public async naturalLanguageToJql(
    origin: string,
    variables: JiraNaturalLanguageToJqlQueryVariables
  ) {
    const client = this.clients.getOrCreate(origin, '/gateway/api/graphql');
    return client.query<JiraNaturalLanguageToJqlQueryResponse['data']>({
      query: naturalLanguageToJqlQuery.useNaturalLanguageToJqlQuery,
      variables,
      context: { tenantBaseUrl: origin },
    });
  }

  public queryIssuesByKeyWithLinks(
    baseUrl: URL,
    variables: JiraIssuesByKeyWithLinksQueryVariables
  ) {
    const client = this.clients.getOrCreate(baseUrl.toString(), '/rest/gira/1');
    return client.query<JiraIssuesByKeyWithLinksQueryResponse['data']>({
      query: issuesByKeyWithLinksQuery.useIssuesByKeyWithLinksQuery,
      variables,
      context: { tenantBaseUrl: baseUrl.toString() },
      fetchPolicy: 'no-cache',
    });
  }

  public queryIssuesByKeyWithChildren(
    baseUrl: URL,
    variables: JiraIssuesByKeyWithChildrenQueryVariables
  ) {
    const client = this.clients.getOrCreate(baseUrl.toString(), '/rest/gira/1');
    return client.query<JiraIssuesByKeyWithChildrenQueryResponse['data']>({
      query: issuesByKeyWithChildrenQuery.useIssuesByKeyWithChildrenQuery,
      variables,
      context: { tenantBaseUrl: baseUrl.toString() },
      fetchPolicy: 'no-cache',
    });
  }
}
